import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import {
  ShoppingCart, Plus, Trash, X, Sparkle, CircleNotch, Storefront,
  CheckCircle, Circle, Receipt, CaretDown, CaretUp, UserCircle
} from '@phosphor-icons/react';
import MaterialsSpendReport from './MaterialsSpendReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_STYLE = {
  draft: 'bg-neutral-100 text-neutral-500',
  assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
};
const STATUS_LABEL = { draft: 'Draft', assigned: 'Assigned', in_progress: 'Shopping', completed: 'Completed' };

const emptyItem = () => ({ name: '', quantity: 1, unit: 'pcs', est_price: '' });

const estTotal = (items) =>
  items.reduce((s, i) => s + (Number(i.est_price) || 0) * (Number(i.quantity) || 1), 0);

const ShoppingListsTab = () => {
  const [lists, setLists] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchLists = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopping-lists`, authHeader());
      setLists(res.data?.lists || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);
  useEffect(() => {
    const id = setInterval(fetchLists, 20000);
    return () => clearInterval(id);
  }, [fetchLists]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shopping list?')) return;
    try {
      await axios.delete(`${API}/shopping-lists/${id}`, authHeader());
      toast({ title: 'Deleted', description: 'Shopping list removed.' });
      fetchLists();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-5" data-testid="shopping-lists-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2 text-neutral-900 dark:text-white">
            <ShoppingCart size={24} weight="fill" className="text-[#EA580C]" />
            Shopping Lists
            <span className="inline-flex items-center gap-1 bg-[#EA580C]/10 text-[#EA580C] text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
              <Sparkle size={10} weight="fill" /> AI
            </span>
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Build a materials list (or let AI draft it), send it to a worker — they check off items, log prices and snap the receipt.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold text-sm rounded-full shadow-sm active:scale-[0.98] transition-all flex-shrink-0"
          data-testid="shopping-new-btn"
        >
          <Plus size={16} weight="bold" /> New List
        </button>
      </div>

      {lists.length === 0 ? (
        <div className="bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-3xl p-10 text-center" data-testid="shopping-empty">
          <ShoppingCart size={40} weight="bold" className="mx-auto text-neutral-300 mb-3" />
          <p className="font-bold text-neutral-600 dark:text-neutral-300 mb-1">No shopping lists yet</p>
          <p className="text-sm text-neutral-500">Send a worker to the store with a clear list — no more forgotten materials.</p>
        </div>
      ) : (
        <>
          <MaterialsSpendReport />
          <div className="space-y-3">
          {lists.map((l) => {
            const picked = l.items.filter((i) => i.picked).length;
            const open = expanded === l.id;
            return (
              <div key={l.id} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-2xl shadow-sm overflow-hidden" data-testid={`shopping-card-${l.id}`}>
                <button onClick={() => setExpanded(open ? null : l.id)} className="w-full flex items-center gap-3 p-4 text-left">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-neutral-900 dark:text-white truncate">{l.title}</span>
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[l.status]}`}>{STATUS_LABEL[l.status]}</span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5 truncate">
                      {l.worker_name ? `→ ${l.worker_name}` : 'Unassigned'}
                      {l.job_title ? ` · ${l.job_title}` : ''}{l.store ? ` · ${l.store}` : ''}
                      {` · ${picked}/${l.items.length} picked`}
                      {l.status === 'completed' && l.total_actual != null ? ` · $${Number(l.total_actual).toLocaleString()} spent` : ''}
                    </p>
                  </div>
                  {l.status === 'completed' && l.total_estimated > 0 && l.total_actual > l.total_estimated && (
                    <span className="text-[10px] font-black uppercase bg-red-50 text-red-600 px-2 py-0.5 rounded-full shrink-0" data-testid={`over-budget-chip-${l.id}`}>
                      +${(l.total_actual - l.total_estimated).toFixed(2)} over
                    </span>
                  )}
                  {open ? <CaretUp size={16} className="text-neutral-400 shrink-0" /> : <CaretDown size={16} className="text-neutral-400 shrink-0" />}
                </button>
                {open && (
                  <div className="px-4 pb-4 space-y-2">
                    {l.receipt_mismatch?.flagged && (
                      <div className="flex items-start gap-2 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid={`receipt-mismatch-${l.id}`}>
                        <Receipt size={14} weight="bold" className="shrink-0 mt-0.5" />
                        <span>
                          Receipt scanned at ${Number(l.receipt_mismatch.receipt_total).toFixed(2)} but ${Number(l.receipt_mismatch.logged_total).toFixed(2)} was logged
                          ({l.receipt_mismatch.diff > 0 ? '+' : '−'}${Math.abs(l.receipt_mismatch.diff).toFixed(2)}) — check the receipt photo.
                        </span>
                      </div>
                    )}
                    {l.items.map((i) => (
                      <div key={i.id} className="flex items-center gap-2 text-sm">
                        {i.picked ? <CheckCircle size={17} weight="fill" className="text-green-500 shrink-0" /> : <Circle size={17} className="text-neutral-300 shrink-0" />}
                        <span className={`flex-1 truncate ${i.picked ? 'text-neutral-400 line-through' : 'text-neutral-700 dark:text-neutral-300'}`}>
                          {i.quantity} {i.unit} — {i.name}
                        </span>
                        {i.actual_price != null && <span className="text-xs font-bold text-green-600 shrink-0">${i.actual_price}</span>}
                      </div>
                    ))}
                    {l.receipt_ids?.length > 0 && (
                      <div className="flex items-center gap-2 pt-1 flex-wrap">
                        <Receipt size={15} className="text-neutral-400" />
                        {l.receipt_ids.map((rid) => {
                          const scan = (l.receipt_scans || []).find((s) => s.file_id === rid);
                          return (
                            <a key={rid} href={`${API}/shopping-lists/receipt/${rid}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#EA580C] underline">
                              View receipt{scan?.total != null ? ` · $${scan.total}${scan.merchant ? ` @ ${scan.merchant}` : ''}` : ''}
                            </a>
                          );
                        })}
                        {(l.receipt_scans || []).some((s) => s.total != null) && (
                          <span className="text-[10px] font-black uppercase bg-[#EA580C]/10 text-[#EA580C] px-1.5 py-0.5 rounded-full" data-testid={`receipt-scanned-badge-${l.id}`}>AI Scanned</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs text-neutral-400">Est. ${estTotal(l.items).toFixed(2)}</span>
                      <button onClick={() => handleDelete(l.id)} className="inline-flex items-center gap-1 text-xs font-bold text-red-500" data-testid={`shopping-delete-${l.id}`}>
                        <Trash size={13} weight="bold" /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </>
      )}

      {showCreate && <CreateListModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchLists(); }} />}
    </div>
  );
};

const CreateListModal = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [store, setStore] = useState('');
  const [jobId, setJobId] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs/my-jobs`, authHeader()).then((r) => setJobs(r.data?.jobs || r.data || [])).catch(() => {});
    axios.get(`${API}/shopping-lists/my-workers`, authHeader()).then((r) => setWorkers(r.data?.workers || [])).catch(() => {});
  }, []);

  const setItem = (idx, field, val) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  };

  const aiDraft = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/shopping-lists/ai-draft`, { description: aiText }, authHeader());
      const drafted = (res.data?.items || []).map((i) => ({ name: i.name, quantity: i.quantity, unit: i.unit, est_price: i.est_price ?? '' }));
      setItems(drafted.length ? drafted : items);
      if (!title.trim()) setTitle(aiText.trim().slice(0, 60));
      toast({ title: 'List drafted', description: `AI suggested ${drafted.length} items — edit before sending.` });
    } catch (e) {
      toast({ title: 'AI draft failed', description: e.response?.data?.detail || 'Try rewording the description.', variant: 'destructive' });
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async () => {
    const clean = items.filter((i) => i.name.trim());
    if (!title.trim() || clean.length === 0) {
      toast({ title: 'Missing info', description: 'Add a title and at least one item.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/shopping-lists`, {
        title: title.trim(),
        store: store.trim() || null,
        job_id: jobId || null,
        worker_id: workerId || null,
        items: clean.map((i) => ({
          name: i.name.trim(),
          quantity: Number(i.quantity) || 1,
          unit: i.unit || 'pcs',
          est_price: i.est_price === '' || i.est_price == null ? null : Number(i.est_price),
        })),
      }, authHeader());
      toast({ title: workerId ? 'List sent' : 'List saved', description: workerId ? 'The worker has been notified.' : 'Saved as draft — assign a worker anytime.' });
      onCreated();
    } catch (e) {
      toast({ title: 'Could not create list', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full h-11 px-3 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-[#EA580C]';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="shopping-create-modal">
      <div className="bg-white dark:bg-neutral-900 w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <ShoppingCart size={20} weight="fill" className="text-[#EA580C]" /> New Shopping List
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800" data-testid="shopping-modal-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* AI draft */}
        <div className="bg-[#EA580C]/5 border border-[#EA580C]/20 rounded-2xl p-3 mb-4">
          <p className="text-xs font-bold text-[#EA580C] uppercase tracking-wide mb-2 flex items-center gap-1">
            <Sparkle size={12} weight="fill" /> AI Draft (optional)
          </p>
          <div className="flex gap-2">
            <input
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder='e.g. "framing a 12x16 deck" or "bathroom tile job, 80 sq ft"'
              className={inputCls}
              data-testid="shopping-ai-input"
            />
            <button
              onClick={aiDraft}
              disabled={aiLoading || !aiText.trim()}
              className="h-11 px-4 bg-[#EA580C] text-white font-bold text-sm rounded-xl disabled:opacity-40 shrink-0 flex items-center gap-1.5"
              data-testid="shopping-ai-draft-btn"
            >
              {aiLoading ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />} Draft
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="List title (e.g. Deck framing run)" className={inputCls} data-testid="shopping-title-input" />
          <div className="relative">
            <Storefront size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={store} onChange={(e) => setStore(e.target.value)} placeholder="Store (optional)" className={`${inputCls} pl-8`} data-testid="shopping-store-input" />
          </div>
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} className={inputCls} data-testid="shopping-job-select">
            <option value="">No job link (optional)</option>
            {jobs.map((j) => <option key={j.id || j._id} value={j.id || j._id}>{j.title || j.job_title}</option>)}
          </select>
          <div className="relative">
            <UserCircle size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <select value={workerId} onChange={(e) => setWorkerId(e.target.value)} className={`${inputCls} pl-8`} data-testid="shopping-worker-select">
              <option value="">Assign worker later</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
        </div>

        {/* Items */}
        <p className="text-xs font-bold uppercase text-neutral-500 mb-2">Items ({items.filter((i) => i.name.trim()).length}) · est. ${estTotal(items).toFixed(2)}</p>
        <div className="space-y-2 mb-3">
          {items.map((it, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input value={it.name} onChange={(e) => setItem(idx, 'name', e.target.value)} placeholder="Item (e.g. 2x4x8 lumber)" className={`${inputCls} flex-1`} data-testid={`shopping-item-name-${idx}`} />
              <input type="number" min="0" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} className={`${inputCls} w-16 px-2 text-center`} title="Qty" />
              <input value={it.unit} onChange={(e) => setItem(idx, 'unit', e.target.value)} className={`${inputCls} w-16 px-2 text-center`} title="Unit" />
              <input type="number" min="0" step="0.01" value={it.est_price} onChange={(e) => setItem(idx, 'est_price', e.target.value)} placeholder="$" className={`${inputCls} w-20 px-2 text-center`} title="Est. price/unit" />
              <button onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))} className="text-neutral-300 hover:text-red-500 shrink-0" disabled={items.length === 1}>
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={() => setItems((prev) => [...prev, emptyItem()])} className="inline-flex items-center gap-1 text-sm font-bold text-[#EA580C] mb-5" data-testid="shopping-add-item-btn">
          <Plus size={14} weight="bold" /> Add item
        </button>

        <button
          onClick={submit}
          disabled={saving}
          className="w-full h-14 bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold text-base rounded-full shadow-sm active:scale-[0.98] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="shopping-submit-btn"
        >
          {saving ? <CircleNotch size={18} className="animate-spin" /> : <ShoppingCart size={18} weight="fill" />}
          {workerId ? 'Send to Worker' : 'Save List'}
        </button>
      </div>
    </div>
  );
};

export default ShoppingListsTab;
